import { AuthenticationDetails, CognitoUser, CognitoUserPool } from 'amazon-cognito-identity-js'

import { getConfig } from "./configMgr";
import {getMessageObject, MessageType} from "../pages/auth/FormLayout/MessageFactory";
import * as Sentry from "@sentry/react";

const { tenantID } = getConfig();

// Helper function to report authentication errors to Sentry with structured context
const reportAuthErrorToSentry = (err, username, errorType, contextName) => {
    // NotAuthorizedException = wrong password / unauthorized — expected user input error, tracked in PostHog instead
    if (err?.name === 'NotAuthorizedException' || err?.code === 'NotAuthorizedException') return;
    Sentry.withScope(scope => {
        scope.setUser({email: username});
        scope.setTag('error_type', errorType);
        scope.setTag('error_name', err.name || 'unknown');
        scope.setContext(contextName, {
            username: username,
            error_name: err.name,
            error_code: err.code,
            error_message: err.message
        });

        // Use captureException for better stack traces, or captureMessage if no exception object
        if (err instanceof Error) {
            Sentry.captureException(err);
        } else {
            const action = errorType === 'login_failure' ? 'Login' : 'Registration';
            Sentry.captureMessage(`${action} failed for ${username}: ${err.name || 'unknown'} - ${err.message || 'no message'}`, "warning");
        }
    });
};

class AWSCognitoProvider {
    constructor(region, userPoolID, clientID) {
        const poolData = {
            UserPoolId: userPoolID,
            ClientId: clientID
        };
        this.userPool = new CognitoUserPool(poolData);
    }

    confirmRegistration = async (email, code) => {
      email = email.toLowerCase();
      const user = new CognitoUser({Username: email, Pool: this.userPool});
      return new Promise((resolve, reject) => {
        user.confirmRegistration(code, true, (err, result) => {
          err ? reject(err) : resolve(result);
        });
      });
    }

    retrieveUserSession = async (forceRefresh = false) => {
        const user = this.userPool.getCurrentUser();
        if (user) {
            console.debug('[CognitoProvider] Retrieving user session', { username: user.getUsername(), forceRefresh });
            return new Promise((resolve, reject) => {
                // getSession implicitly refreshes session if the token is expired
                // https://github.com/aws/amazon-cognito-identity-js/blob/master/src/CognitoUser.js#L1059
                // However, if forceRefresh is true, we explicitly call refreshSession first
                // to ensure we get a new token (e.g., after a 401/403 error)
                const getSessionAndAttributes = (err, session) => {
                    if (err) {
                        // NetworkError is a browser-level fetch failure (offline, DNS, CORS) — transient
                        // and handled gracefully by UserStore; warn instead of error to avoid Sentry noise
                        const logFn = err.code === 'NetworkError' ? console.warn : console.error;
                        logFn(`[CognitoProvider] getSession failed: ${err.name} - ${err.message}`, {
                            username: user.getUsername(),
                            forceRefresh
                        });
                        reject(err);
                    }
                    else {
                        const tokenExp = session?.getIdToken()?.getExpiration();
                        const now = Math.floor(Date.now() / 1000);
                        const timeToExpiry = tokenExp ? tokenExp - now : null;
                        console.debug('[CognitoProvider] getSession succeeded', {
                            username: user.getUsername(),
                            tokenExpiresIn: timeToExpiry ? `${Math.floor(timeToExpiry / 60)}min` : 'unknown',
                            forceRefresh
                        });
                        user.getUserAttributes((err, attrs = []) => {
                            if (err) {
                                if (err.message?.includes('Access Token has expired')) {
                                    console.debug('[CognitoProvider] Access token expired before getUserAttributes, retrying after refresh');
                                    user.refreshSession(session.getRefreshToken(), (refreshErr, refreshedSession) => {
                                        if (refreshErr) {
                                            console.warn('[CognitoProvider] getUserAttributes retry refresh failed (non-fatal)', refreshErr.message);
                                            resolve({session, user, attributes: {}});
                                            return;
                                        }
                                        user.getUserAttributes((retryErr, retryAttrs = []) => {
                                            if (retryErr) {
                                                console.warn('[CognitoProvider] getUserAttributes failed after refresh (non-fatal)', retryErr.message);
                                            }
                                            const attributes = (retryAttrs || []).reduce((result, entry) => ({...result, [entry['Name']]: entry['Value']}), {});
                                            resolve({session: refreshedSession, user, attributes});
                                        });
                                    });
                                } else {
                                    // NetworkError is a transient browser-level fetch failure (offline, DNS, CORS) -
                                    // same rationale as the getSession logFn above; log only, no Sentry noise (PLEX-3949).
                                    const logFn = err.code === 'NetworkError' ? console.debug : console.warn;
                                    logFn('[CognitoProvider] getUserAttributes failed (non-fatal)', err.message);
                                    resolve({session, user, attributes: {}});
                                }
                            } else {
                                const attributes = (attrs || []).reduce((result, entry) => ({...result, [entry['Name']]: entry['Value']}), {});
                                resolve({session, user, attributes});
                            }
                        });
                    }
                };

                if (forceRefresh) {
                    // Force a token refresh by explicitly calling refreshSession with the refresh token
                    console.debug('[CognitoProvider] Force refresh requested, getting current session first');
                    user.getSession((err, session) => {
                        if (err || !session) {
                            // Missing ID Token is a known transient state (e.g. localStorage cleared);
                            // the auth flow handles this gracefully, so capture as handled warning (WEBAPP-K8, WEBAPP-78).
                            // NotAuthorizedException = expired refresh token — expected session expiry, not a bug.
                            // NetworkError = transient browser-level fetch failure (WEBAPP-7P/PLEX-5834) — same
                            // rationale as the getSessionAndAttributes handler above, log only, no Sentry noise.
                            // Use captureMessage instead of console.warn — Sentry console integration
                            // treats console.warn as unhandled and stringifies the extra arg as [object Object] (PLEX-4036).
                            if (err?.name !== 'NotAuthorizedException' && err?.code !== 'NotAuthorizedException'
                                && err?.code !== 'NetworkError') {
                                Sentry.withScope(scope => {
                                    scope.setLevel('warning');
                                    scope.setContext('forceRefreshFailure', { code: err?.code, message: err?.message });
                                    Sentry.captureMessage('[CognitoProvider] Failed to get session for force refresh', 'warning');
                                });
                            } else if (err?.code === 'NetworkError') {
                                console.warn(`[CognitoProvider] force refresh getSession failed: ${err.name} - ${err.message}`, {
                                    username: user.getUsername()
                                });
                            }
                            reject(err ?? new Error('[CognitoProvider] getSession returned no session for force refresh'));
                            return;
                        }
                        const refreshToken = session.getRefreshToken();
                        console.debug('[CognitoProvider] Calling refreshSession with refresh token');
                        user.refreshSession(refreshToken, getSessionAndAttributes);
                    });
                } else {
                    // Normal behavior - getSession will auto-refresh if needed
                    user.getSession(getSessionAndAttributes);
                }
            });
        }
        console.debug('[CognitoProvider] No current user found in localStorage');
        return null;
    }

    signIn(username, password, navigate, verificationCode = null, newPassword = null, rememberMe = true) {
        username = username.toLowerCase();
        const validationData = verificationCode ? {token: verificationCode} : {};
        const authenticationDetails = new AuthenticationDetails({
            Username: username,
            Password: password,
            ValidationData: validationData
        });
        const userData = { Username: username, Pool: this.userPool };
        const cognitoUser = new CognitoUser(userData);
        if (tenantID !== "") {
            validationData['tenant_id'] = tenantID;
        }
        return new Promise((resolve, reject) => {
            cognitoUser.authenticateUser(authenticationDetails, {
                onSuccess: session => {

                    if (!rememberMe) {
                        window.addEventListener("beforeunload", ev => this.signOut(username))
                    }
                    cognitoUser.getUserAttributes((err, attrs = []) => {
                        if (err) {
                            reject(err);
                        } else {
                            const attributes = attrs.reduce((result, entry) => ({...result, [entry['Name']]: entry['Value']}), {});
                            resolve({session, user: cognitoUser, attributes});
                        }
                    });
                },
                newPasswordRequired: (userAttributes, requiredAttributes) => {
                    // If we've already got the user's new password, install it as the new one
                    if (newPassword) {
                        delete userAttributes.email_verified;
                        delete userAttributes.email;
                        cognitoUser.completeNewPasswordChallenge(newPassword, userAttributes, {
                            onSuccess: result => {
                                this.retrieveUserSession()
                                  .then(result => resolve(result))
                                  .catch(err => reject(err));
                            },
                            onFailure: err => {
                                // Pass plain object to console.warn — Sentry console integration
                                // captures raw Error objects as exceptions (PLEX-4036).
                                console.warn("Unable to change password", { code: err?.code, message: err?.message });
                                // NotAuthorizedException = expected auth rejection, not a bug
                                if (err?.name !== 'NotAuthorizedException' && err?.code !== 'NotAuthorizedException') {
                                    Sentry.captureMessage(`Password change failed for ${username}: ${err}`, "warning");
                                }
                                reject(err);
                            }
                        });
                    }
                    else {
                        // Present the user with the password change form to get a new password
                        navigate("/changepass", {
                            replace: true,
                            state: {
                                username, oldPassword: password, userAttributes, requiredAttributes,
                                message: getMessageObject(MessageType.PasswordResetRequiredException),
                            },
                        });
                    }
                },
                onFailure: err => {
                    // PLEX-3368: Improve login error handling and Sentry reporting
                    reportAuthErrorToSentry(err, username, 'login_failure', 'login_error');

                    // Admin reset (AdminResetUserPassword) puts the user in RESET_REQUIRED state.
                    // The reset code was already emailed — go straight to /resetpass so the user
                    // can enter the code they received. Do NOT call forgotPassword() here; that
                    // would invalidate the existing code and send a redundant second email.
                    if (err.name === MessageType.PasswordResetRequiredException) {
                        navigate("/resetpass", {
                            replace: true,
                            state: {
                                username,
                                accessLinkSent: true,
                                message: {
                                    type: "info",
                                    message: `A password reset code was sent to ${username}. Enter it below along with your new password. If you didn't receive it, use the resend button.`,
                                }
                            },
                        });
                    }
                    else {
                        reject(err);
                    }
                },
            });
        });
    }

    register(username, password) {
        username = username.toLowerCase();
        const origin = window.location.origin === "http://localhost:3000"
                       ? "https://plexsearch.com" : window.location.origin;
        const attributeList = [
            { Name: 'email', Value: username },
            { Name: 'website', Value: window.location.origin }
        ];

        return new Promise((resolve, reject) => {
            this.userPool.signUp(username, password, attributeList, null,
                                 (err, result) => {
                                     if (err) {
                                         // PLEX-3368: Improve registration error reporting
                                         reportAuthErrorToSentry(err, username, 'registration_failure', 'registration_error');
                                         reject(err);
                                     }
                                     else {
                                         Sentry.withScope(scope => {
                                             scope.setLevel("info");
                                             scope.setUser({email: username});
                                             scope.setTag('auth_flow', 'registration');
                                             scope.setTag('registered_user', username);
                                             scope.setFingerprint(['registration-started']);
                                             Sentry.captureMessage('New user registration');
                                         });
                                         resolve(result);
                                     }
                                 });
        });
    }

    signOut(username) {
        // Sign out the specified user
        if (username) {
            const cognitoUser = new CognitoUser({ Username: username.toLowerCase(), Pool: this.userPool });
            cognitoUser.signOut();
        }
        // Also sign out any current user that might be in localStorage
        const currentUser = this.userPool.getCurrentUser();
        if (currentUser) {
            currentUser.signOut();
        }
        Sentry.setUser(null);
    }

    resendConfirmation(username) {
        username = username.toLowerCase();
        const cognitoUser = new CognitoUser({ Username: username, Pool: this.userPool });
        return new Promise((resolve, reject) => {
            cognitoUser.resendConfirmationCode((err, result) => {
                if (err) {
                    reject(err);
                }
                else {
                    resolve(result);
                }
            });
        });
    }

    sendVerificationCode(username) {
        username = username.toLowerCase();
        const cognitoUser = new CognitoUser({ Username: username, Pool: this.userPool });
        return new Promise((resolve, reject) => {
            cognitoUser.forgotPassword({onSuccess: resolve, onFailure: reject});
        });
    }

    confirmNewPassword(username, verificationCode, newPassword) {
        username = username.toLowerCase();
        const cognitoUser = new CognitoUser({ Username: username, Pool: this.userPool });
        return new Promise((resolve, reject) => {
            cognitoUser.confirmPassword(verificationCode, newPassword, {
                onSuccess: resolve,
                onFailure: reject
            });
        });
    }

    changePassword(username, oldPassword, newPassword, navigate) {
        const cognitoUser = this.userPool.getCurrentUser();
        if (cognitoUser) {
            return new Promise((resolve, reject) => {
                cognitoUser.getSession((err, session) => {
                    if (err) {
                        reject(err);
                        return;
                    }
                    cognitoUser.changePassword(oldPassword, newPassword, (err, result) => {
                        // "result" is just "SUCCESS", which is useless, so return the session instead
                        if (err) {
                            reject(err);
                            return;
                        }
                        resolve({session, user: cognitoUser});
                    });
                });
            });
        }
        return this.signIn(username, oldPassword, navigate, null, newPassword, true);
    }
}

export default AWSCognitoProvider;
